import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { WeekDateRangeResponse, YearPlanResponse } from '../../api/generated';
import { INTERVENTION_ICON_REGISTRY } from '../../constants/interventionIconConstants';
import { GanttWeekHeaderCells } from '../GanttChart/GanttWeekHeader';
import { GanttGroupLabel } from '../GanttChart/GanttGroupLabel';
import { GanttGroupRow } from '../GanttChart/GanttGroupRow';
import type { GanttGroup } from '../GanttChart/types';
import { computePerWeekColumnWidths } from '../../utils/ganttUtils';
import type { TreatmentPlanTemplateDraft } from './templateDraftTypes';
import styles from './TemplateGanttPreview.module.css';

interface TemplateGanttPreviewProps {
  template: TreatmentPlanTemplateDraft;
}

export const TemplateGanttPreview = ({ template }: TemplateGanttPreviewProps) => {
  const { t } = useTranslation();

  const totalWeeks = template.weeks.length;

  const weeks: WeekDateRangeResponse[] = useMemo(
    () =>
      template.weeks.map((_, idx) => ({
        weekNumber: idx + 1,
        startDate: '',
        endDate: ''
      })),
    [template.weeks]
  );

  const yearPlanForHeader: YearPlanResponse = useMemo(
    () => ({ weeks, treatmentPlans: [], assignedPatients: [], lastModifiedDate: '' }),
    [weeks]
  );

  const ganttGroup: GanttGroup = useMemo(
    () => ({
      templateId: template.id ?? 'draft',
      templateName: template.name || t('config-unnamed-template'),
      series: [
        {
          planIndex: 0,
          treatmentPlanId: template.id ?? 'draft',
          groups: template.weeks.flatMap((week, weekIdx) =>
            week.groups.map((dg) => ({
              id: dg._key,
              name: dg.group.name,
              icon:
                dg.group.icon && dg.group.icon in INTERVENTION_ICON_REGISTRY
                  ? dg.group.icon
                  : undefined,
              barSize: dg.group.barSize,
              interventions: [],
              weekNumber: weekIdx + 1
            }))
          ),
          seriesLabel: '',
          strokeColor: template.strokeColor,
          fillColor: template.fillColor,
          highlightColor: template.highlightColor
        }
      ]
    }),
    [template, t]
  );

  const weekGridTemplateColumns = useMemo(() => {
    const widths = computePerWeekColumnWidths([ganttGroup], totalWeeks);
    return widths.map((w) => `${String(w)}px`).join(' ');
  }, [ganttGroup, totalWeeks]);

  if (totalWeeks === 0) {
    return <p className={styles.empty}>{t('config-builder-no-weeks')}</p>;
  }

  return (
    <div className={styles.layout}>
      <div className={styles.cornerPlaceholder} />
      <GanttGroupLabel group={ganttGroup} isEditMode={false} />
      <div
        className={styles.scrollArea}
        style={{ gridTemplateColumns: weekGridTemplateColumns, gridRow: '1 / span 2' }}
      >
        <GanttWeekHeaderCells yearPlan={yearPlanForHeader} showKpi={false} />
        <GanttGroupRow group={ganttGroup} totalWeeks={totalWeeks} weeks={weeks} readOnly />
      </div>
    </div>
  );
};
